import { lazy, Suspense, useEffect } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { trackPageView } from "@/lib/tracking";
import ScrollToTop from "@/components/ScrollToTop";
import Index from "./pages/Index.tsx";

// Lazy-load all non-homepage routes for smaller initial bundle
const QuotePage = lazy(() => import("./pages/QuotePage.tsx"));
const PrivacyPage = lazy(() => import("./pages/PrivacyPage.tsx"));
const TermsPage = lazy(() => import("./pages/TermsPage.tsx"));
const ServicePage = lazy(() => import("./pages/ServicePage.tsx"));
const SubServicePage = lazy(() => import("./pages/SubServicePage.tsx"));
const CityPage = lazy(() => import("./pages/CityPage.tsx"));
const CityServicePage = lazy(() => import("./pages/CityServicePage.tsx"));
const AllCitiesPage = lazy(() => import("./pages/AllCitiesPage.tsx"));
const StatePage = lazy(() => import("./pages/StatePage.tsx"));
const BlogIndex = lazy(() => import("./pages/BlogIndex.tsx"));
const BlogPost = lazy(() => import("./pages/BlogPost.tsx"));
const BlogCategoryPage = lazy(() => import("./pages/BlogCategoryPage.tsx"));
const AboutPage = lazy(() => import("./pages/AboutPage.tsx"));
const ContactPage = lazy(() => import("./pages/ContactPage.tsx"));
// const JoinPage = lazy(() => import("./pages/JoinPage.tsx")); // Hidden until mybookofbiz CRM is ready
const GuidePage = lazy(() => import("./pages/GuidePage.tsx"));
const GuidesIndex = lazy(() => import("./pages/GuidesIndex.tsx"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard.tsx"));
const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage.tsx"));
const SitemapPage = lazy(() => import("./pages/SitemapPage.tsx"));
const NotFound = lazy(() => import("./pages/NotFound.tsx"));
import LegacyRedirect from "./components/LegacyRedirect";

// Old WordPress paths Google still has indexed. Each maps to its canonical equivalent.
const legacyRedirects: Array<[string, string]> = [
  ["/about-us", "/about"],
  ["/about-us/", "/about"],
  ["/contact-us", "/contact"],
  ["/contact-us/", "/contact"],
  ["/Home", "/"],
  ["/home/", "/"],
  ["/auto-insurance", "/services/auto-insurance"],
  ["/auto-insurance/", "/services/auto-insurance"],
  ["/home-insurance", "/services/home-insurance"],
  ["/home-insurance/", "/services/home-insurance"],
  ["/business-insurance", "/services/business-insurance"],
  ["/business-insurance/", "/services/business-insurance"],
  ["/health-insurance", "/services/health-insurance"],
  ["/health-insurance/", "/services/health-insurance"],
  ["/life-insurance", "/services/life-insurance"],
  ["/life-insurance/", "/services/life-insurance"],
  ["/group-benefits", "/services/group-benefits"],
  ["/group-benefits/", "/services/group-benefits"],
  ["/blog/", "/blog"],
  ["/quote/", "/quote"],
];

const queryClient = new QueryClient();

const PageTracker = () => {
  const location = useLocation();
  useEffect(() => {
    trackPageView(location.pathname);
  }, [location.pathname]);
  return null;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <ScrollToTop />
        <PageTracker />
        <Suspense fallback={<div className="min-h-screen bg-background" />}>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/quote" element={<QuotePage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/contact" element={<ContactPage />} />
            {/* <Route path="/join" element={<JoinPage />} /> */}{/* Hidden until mybookofbiz CRM is ready */}
            <Route path="/services/:slug" element={<ServicePage />} />
            <Route path="/coverage/:slug" element={<SubServicePage />} />
            <Route path="/az/:slug" element={<CityPage />} />
            <Route path="/az/:citySlug/:serviceSlug" element={<CityServicePage />} />
            <Route path="/cities" element={<AllCitiesPage />} />
            <Route path="/states/:slug" element={<StatePage />} />
            <Route path="/blog" element={<BlogIndex />} />
            <Route path="/blog/category/:slug" element={<BlogCategoryPage />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/guides" element={<GuidesIndex />} />
            <Route path="/guides/:slug" element={<GuidePage />} />
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/sitemap" element={<SitemapPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            {legacyRedirects.map(([from, to]) => (
              <Route key={from} path={from} element={<LegacyRedirect to={to} />} />
            ))}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
